<template>
  <div class="c-settings">
    <v-dialog
      v-model="showSettings"
      fullscreen
      hide-overlay
      content-class="dialog-content"
      transition="dialog-bottom-transition"
    >
      <v-container>
        <v-row justify="space-between">
          <v-col>
            <v-btn
              color="on-surface"
              icon
              @click.prevent="closeSettings"
            >
              <v-icon x-large>mdi-chevron-left</v-icon>
            </v-btn>
          </v-col>
          <v-col class="text-center">
            <h3>Settings</h3>
          </v-col>
          <v-col></v-col>
        </v-row>

        <v-row>
          <v-col cols="auto">
            <v-card>
              <v-card-title>Reportings</v-card-title>
              <v-card-text>
                <v-select
                  class="mt-4"
                  label="Default activity type"
                  v-model="settings.defaultActivityType"
                  :items="activityTypes"
                  item-text="Description"
                  item-value="Activity_Type"
                  @change="changeSettings(true)"
                ></v-select>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="auto">
            <v-card>
              <v-card-title>Browser Extension</v-card-title>
              <v-card-text>
                You can
                <a href="/extension/extension.zip" download>download</a>
                the browser extension (at the moment only for Chrome)<br />
                and install it according to the
                <a
                  href="#"
                  @click.prevent="showInstructionForExtension = !showInstructionForExtension"
                >
                  instructions
                </a>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="auto"
            md="6"
            lg="4"
            v-if="showJiraAccessWidget && jiraApiLogin && jiraApiToken"
          >
            <v-card>
              <v-card-title>Jira Access</v-card-title>
              <v-card-text class="py-4">
                Please fill all the fields to get access to the Jira REST API.
                To get API Token go to the
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  class="px-1"
                  >API Tokens manager</a
                >
                and create a new one. Then paste it to the "Jira API Token"
                field.

                <div class="d-flex align-center mt-4">
                  <v-chip
                    v-if="tokenStatus !== null"
                    small
                    :color="tokenStatus === 'valid' ? 'success' : tokenStatus === 'no_integration' ? 'warning' : 'error'"
                    text-color="white"
                    class="mr-2"
                  >
                    <v-icon small left>{{ tokenStatus === 'valid' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                    {{ {
                      valid: 'Token is valid',
                      unauthorized: 'Token is expired or invalid',
                      no_token: 'No token saved',
                      no_integration: 'Token saved, but no Jira integration configured to verify',
                      error: 'Could not verify token'
                    }[tokenStatus] }}
                  </v-chip>
                  <v-btn small text :loading="checkingToken" :disabled="checkingToken" @click="checkTokenValidity">
                    <v-icon small left>mdi-refresh</v-icon>
                    Check token
                  </v-btn>
                </div>

                <v-text-field
                  class="mt-5 pt-3"
                  dense
                  hide-details
                  label="Jira login"
                  v-model="jiraApiLogin"
                ></v-text-field>
                <v-text-field
                  class="mt-5 pt-3"
                  dense
                  hide-details
                  label="Jira API Token"
                  type="password"
                  v-model="jiraApiToken"
                  @input="tokenStatus = null"
                ></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  small
                  color="primary"
                  :disabled="!jiraApiLogin || !jiraApiToken"
                  @click="updateJiraAccess"
                >
                  Update
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <v-col cols="auto">
            <v-card>
              <v-card-title>Themes</v-card-title>
              <v-card-text>
                <v-select
                  class="mt-4"
                  label="Default Theme"
                  v-model="settings.theme"
                  :items="themes"
                  item-text="Description"
                  item-value="value"
                  @change="changeTheme"
                ></v-select>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="auto" md="6" lg="4">
            <v-card>
              <v-card-title>Tasks</v-card-title>
              <v-card-text>
                <div class="file-input mt-4">
                  <v-text-field
                    v-model="displayedValue"
                    label="Default Task"
                    solo
                    hide-details
                    :readonly="!!(settings && settings.defaultTask)"
                    @input="toggleTree('input')"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon
                        v-if="settings && settings.defaultTask"
                        color="success"
                      >
                        mdi-file
                      </v-icon>
                    </template>
                    <template v-slot:append>
                      <v-btn
                        v-if="displayedValue"
                        rounded
                        icon
                        color="primary"
                        @click="toggleTree('clear')"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <v-btn
                        rounded
                        icon
                        color="primary"
                        @mouseup.stop @click.prevent.stop="toggleTree('expand')"
                      >
                        <v-icon
                          size="30"
                          :class="{ 'rotate': isArrowUp }"
                        >
                          mdi-chevron-down
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                  <v-expansion-panels
                    v-show="showTree"
                    class="panels-dropdown"
                    multiple
                    accordion
                    v-model="expandedPanels"
                  >
                    <v-expansion-panel
                      v-show="showProject(project)"
                      v-for="(project, projectKey) in filteredProjectsAndTasks"
                      :key="projectKey"
                    >
                      <v-expansion-panel-header>
                        {{ project.Project_Name }}
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-treeview
                          :items="project.Tasks"
                          item-key="Task_ID"
                          item-text="Name"
                          :open="expandedNodes"
                          activatable
                          return-object
                          dense
                          open-on-click
                          @update:active="selectDefaultTask"
                        >
                          <template v-slot:prepend="{ item, open }">
                            <v-icon
                              v-if="item.Is_Folder"
                              color="primary"
                            >
                              {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                            </v-icon>
                            <v-icon
                              v-else
                              color="success"
                            >
                              mdi-file
                            </v-icon>
                          </template>
                        </v-treeview>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-dialog>

    <v-dialog v-model="showInstructionForExtension" max-width="500">
      <v-card class="how-to-install-extension">
        <v-card-title>How to install extension</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <ol>
            <li>
              <a href="/extension/extension.zip" download>Download</a> extension
            </li>
            <li>Unzip downloaded file</li>
            <li>
              Open the Extension Management page by navigating to
              <code>chrome://extensions</code>.
              <ul>
                <li>
                  Alternatively, open this page by clicking on the Extensions
                  menu button and selecting <strong>Manage Extensions</strong>.
                </li>
                <li>
                  Or go to Chrome menu → <strong>More Tools</strong> →
                  <strong>Extensions</strong>
                </li>
              </ul>
            </li>
            <li>
              Enable Developer Mode by toggling
              <strong>Developer mode</strong>.
            </li>
            <li>
              Click the <strong>Load unpacked</strong> button and select the
              extension directory.
            </li>
          </ol>
          <img
            src="@/assets/how-to-install-extension.png"
            alt="how to install extension"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'Settings',
  data () {
    return {
      showInstructionForExtension: false,
      jiraApiLogin: null,
      jiraApiToken: null,
      tokenStatus: null,
      checkingToken: false,
      themes: [
        { Description: 'Automatic', value: 'automatic' },
        { Description: 'Light', value: 'light' },
        { Description: 'Dark', value: 'dark' }
      ],
      showTree: false,
      search: '',
      isArrowUp: false,
      listOfProjectsAndTasks: [],
      filteredProjectsAndTasks: [],
      expandedPanels: [],
      expandedNodes: [],
      taskChanged: false
    }
  },
  computed: {
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    showJiraAccessWidget () {
      const roles = this.$store.getters.getEmployeeRoles
      const rolesArr = Object.values(roles)
        .filter((r) => r.val)
        .map((r) => r.name.toLowerCase())
      return (
        rolesArr.includes('financier') || rolesArr.includes('manager')
      )
    },
    showSettings: {
      get () {
        if (this.$store.getters.showSettings) {
          if (this.showJiraAccessWidget) {
            this.checkIfHasAccessToJira()
          }
          if (!this.listOfProjectsAndTasks.length) {
            this.getProjectsAndTasks()
          }
        }
        return this.$store.getters.showSettings
      },
      set (val) {
        this.$store.dispatch('showSettings', { show: val })
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    settings: {
      get () {
        return this.$store.getters.getSettings
      },
      set (val) {
        this.$store.dispatch('setSettings', { settings: val })
      }
    },
    displayedValue: {
      get () {
        if (this.settings && this.settings.defaultTask) {
          return this.settings.defaultTask.Name
        }
        return this.search
      },
      set (value) {
        if (this.settings && this.settings.defaultTask) {
          this.$set(this.settings, 'defaultTask', null)
        }
        this.search = value
        this.updateExpandedValues()
      }
    }
  },
  methods: {
    closeSettings () {
      this.$store.dispatch('showSettings', { show: false })
      if (this.taskChanged) {
        window.location.reload()
      }
    },
    checkIfHasAccessToJira () {
      this.axios
        .post(
          '/api/integrations/get-jira-access',
          { Employee_ID: this.user.employee_id },
          { headers: { Authorization: 'Bearer ' + this.user.token } }
        )
        .then((res) => {
          if (res.data && res.data[0]) {
            this.jiraApiLogin = res.data[0].Jira_Login
            this.jiraApiToken = res.data[0].API_Token
            this.checkTokenValidity()
          }
        })
        .catch((error) => {
          this.$store.dispatch('setError', {
            errorMsg: error.message + ' - ' + error.response.data
          })
        })
    },
    checkTokenValidity () {
      this.checkingToken = true
      this.axios.post('/api/integrations/verify-jira-token', {
        Employee_ID: this.user.employee_id
      }, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(res => {
        this.tokenStatus = res.data.valid ? 'valid' : (res.data.reason || 'error')
      }).catch(() => {
        this.tokenStatus = 'error'
      }).finally(() => {
        this.checkingToken = false
      })
    },
    updateJiraAccess () {
      this.axios
        .post(
          '/api/integrations/jira-access',
          {
            Employee_ID: this.user.employee_id,
            Jira_Login: this.jiraApiLogin,
            API_Token: this.jiraApiToken
          },
          { headers: { Authorization: 'Bearer ' + this.user.token } }
        )
        .then(() => {
          this.$store.dispatch('setSuccess', {
            successMsg: 'Your access information successfully updated'
          })
        })
        .catch((error) => {
          this.$store.dispatch('setError', {
            errorMsg: error.message + ' - ' + error.response.data
          })
        })
    },
    changeSettings (updateReports) {
      this.axios
        .put(
          '/api/settings',
          { Settings: this.settings },
          { headers: { Authorization: 'Bearer ' + this.user.token } }
        )
        .then(() => {
          this.$store.dispatch('updateReports', {
            updateReports: updateReports
          })
        })
        .catch((error) => {
          this.$store.dispatch('setError', {
            errorMsg: error.message + ' - ' + error.response.data
          })
        })
    },
    changeTheme () {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')

      if (this.settings.theme === 'automatic') {
        this.$vuetify.theme.dark = prefersDark.matches
        localStorage.setItem('theme', 'automatic')

        prefersDark.onchange = (e) => {
          this.$vuetify.theme.dark = e.matches
        }
      } else {
        this.$vuetify.theme.dark = this.settings.theme === 'dark'
        localStorage.setItem('theme', this.settings.theme)
      }

      this.changeSettings(true)
    },
    getProjectsAndTasks () {
      this.axios.get('/api/tasks/reps/list-of-projects-and-tasks', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.filteredProjectsAndTasks = response.data
        this.listOfProjectsAndTasks = response.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    toggleTree (eventType) {
      if (eventType === 'clear') {
        this.$set(this.settings, 'defaultTask', null)
        this.search = ''
        this.changeSettings(false)
        this.updateExpandedValues()
        this.taskChanged = true
      } else if (eventType === 'input' && !this.showTree) {
        this.showTree = !this.showTree
        this.isArrowUp = !this.isArrowUp
        this.updateExpandedValues()
      } else if (eventType === 'expand') {
        this.showTree = !this.showTree
        this.isArrowUp = !this.isArrowUp
        this.updateExpandedValues()
      }
    },
    selectDefaultTask (e) {
      if (e.length && !e[0].Is_Folder) {
        const minimizedTask = {
          Task_ID: e[0].Task_ID,
          Name: e[0].Name,
          Project_ID: e[0].Project_ID
        }

        this.$set(this.settings, 'defaultTask', minimizedTask)

        this.showTree = false
        this.isArrowUp = false

        this.changeSettings(false)
        this.clearExpandedValues()
        this.taskChanged = true
      }
    },
    clearExpandedValues () {
      this.expandedPanels = []
      this.expandedNodes = []
    },
    updateExpandedValues () {
      this.clearExpandedValues()
      this.filteredProjectsAndTasks = JSON.parse(JSON.stringify(this.listOfProjectsAndTasks))
      if (!this.search) return

      this.filteredProjectsAndTasks = JSON.parse(JSON.stringify(this.listOfProjectsAndTasks))
        .map(project => {
          if (this.showProject(project)) {
            const filteredProject = { ...project }
            if (!project.Project_Name.toLowerCase().includes(this.search.toLowerCase())) {
              filteredProject.Tasks = this.filterTasks(filteredProject.Tasks, this.search.toLowerCase())
            }
            return filteredProject
          }
          return null
        })
        .filter(project => project !== null)

      this.filteredProjectsAndTasks.forEach((project, index) => {
        if (this.showProject(project)) {
          this.expandedPanels.push(index)
          this.expandTaskFolders(project.Tasks, this.search.toLowerCase())
        }
      })
    },
    filterTasks (tasks, search) {
      return tasks.reduce((filtered, task) => {
        const isTaskMatch = task.Name.toLowerCase().includes(search)
        const filteredChildren = task.children ? this.filterTasks(task.children, search) : []
        if (!task.Is_Folder && (isTaskMatch || filteredChildren.length > 0)) {
          filtered.push({
            ...task,
            children: filteredChildren
          })
        } else if (task.Is_Folder && filteredChildren.length > 0) {
          filtered.push({
            ...task,
            children: filteredChildren
          })
        }
        return filtered
      }, [])
    },
    expandTaskFolders (tasks, search) {
      tasks.forEach(task => {
        if (task.Name.toLowerCase().includes(search)) {
          if (task.Parent_Task_ID) this.expandParentFolders(task.Parent_Task_ID)
        }
        if (task.children) this.expandTaskFolders(task.children, search)
      })
    },
    expandParentFolders (parentId) {
      this.expandedNodes.push({ Task_ID: parentId })
      const parentTask = this.findTaskById(parentId)
      if (parentTask && parentTask.Parent_Task_ID) this.expandParentFolders(parentTask.Parent_Task_ID)
    },
    findTaskById (taskId) {
      let foundTask = null
      const searchTask = tasks => {
        for (const task of tasks) {
          if (task.Task_ID === taskId) {
            foundTask = task
            break
          }
          if (task.children) searchTask(task.children)
        }
      }
      this.filteredProjectsAndTasks.forEach(project => {
        searchTask(project.Tasks)
      })
      return foundTask
    },
    showProject (project) {
      const searchLower = this.search.toLowerCase()
      return project.Project_Name.toLowerCase().includes(searchLower) ||
             this.containsTask(project.Tasks, searchLower)
    },
    containsTask (tasks, search) {
      for (const task of tasks) {
        if (task.Name.toLowerCase().includes(search)) return true
        if (task.children && this.containsTask(task.children, search)) return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.c-settings {
  background-color: var(--v-theme-background);
  color: var(--v-theme-on-surface);
}

.v-menu__content {
  background-color: var(--v-theme-surface);
  color: var(--v-theme-on-surface);
}

.v-card {
  min-height: 100%;
  background-color: var(--v-theme-surface);
  color: var(--v-theme-on-surface);
  box-shadow: 0 1px 3px rgba(var(--v-theme-on-surface-rgb), 0.1);
}

.how-to-install-extension {
  background-color: var(--v-theme-surface);
  color: var(--v-theme-on-surface);

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 0 auto;
  }

  ol {
    margin: 0;
    padding: 16px 24px 10px;
  }

  a {
    color: var(--v-theme-primary);
    text-decoration: underline;
  }
}

.file-input {
  position: relative;
}

.rotate {
  transform: rotate(-180deg);
  transition: transform 0.3s ease-in-out;
}

.panels-dropdown {
  position: absolute;
  top: 50px;
  width: 100%;
  z-index: 10;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 5px 19px -7px rgba(135, 135, 135, 0.47);
}
</style>
